import { CancelablePromise } from "cancelable-promise";
import { toast } from "react-toastify";
import { serializeError } from "serialize-error";
import { v4 as uuidv4 } from "uuid";
import { withQuery } from "with-query";

import CacheRules from "@/cache-rules";
import { humanizeError } from "@/lib/humanizeError";
import { triggerDataRefresh } from "@/lib/useData";
import { triggerGotUnauthorized } from "@/store/features/authSlice";
import { store } from "@/store/store";

type DeferT<T = unknown> = {
  id: string;
  promise: CancelablePromise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

type CacheEntryT<T = unknown> = {
  data: T;
  expiresAt: number;
};

export class UnauthorizedError extends Error {}

class RequestCache {
  private readonly incomingRequests = new Map<string, DeferT[]>();
  private readonly outgoingRequests = new Map<
    string,
    { abortController: AbortController }
  >();
  private readonly channelMap = new Map<string, DeferT["promise"]>(); // channelId -> req

  private readonly responseCache = new Map<string, CacheEntryT>();

  fetch(
    url: string,
    options?: {
      channelId?: string;
      headers?: { [k: string]: string };
      method?: "GET" | "POST" | "PUT" | "DELETE";
      body?: string | FormData | File;
      noSideEffects?: boolean;
      noCache?: boolean;
    },
  ): CancelablePromise {
    const incomingRequestId = uuidv4();

    options = { ...options };
    options.method ??= "GET";

    const reqHash = (() => {
      let hash = options.method + ":" + url;
      if (options.method !== "GET" && options.body)
        hash += ":" + JSON.stringify(options.body);
      return hash;
    })();

    if (!this.incomingRequests.get(reqHash)) this.incomingRequests.set(reqHash, []);

    if (options.method === "GET" && !options.noCache) {
      const parsedUrl = new URL(url);
      const cacheRule = CacheRules.find(({ path }) => path === parsedUrl.pathname);
      if (cacheRule) {
        const maybeCachedEntity = this.responseCache.get(reqHash);
        if (maybeCachedEntity) {
          const { data, expiresAt } = maybeCachedEntity;
          const isNotExpired = expiresAt > Date.now();
          if (isNotExpired) {
            return new CancelablePromise(resolve => resolve(data));
          }
        }
        let resolve: DeferT["resolve"] = () => {
          throw new Error("Implementation error");
        };
        let reject: DeferT["reject"] = () => {
          throw new Error("Implementation error");
        };
        const promise = new CancelablePromise((resolve1, reject1) => {
          resolve = resolve1;
          reject = reject1;
        });
        promise.then(data => {
          this.responseCache.set(reqHash, {
            data,
            expiresAt:
              Date.now() + (cacheRule.ttl ? cacheRule.ttl : 1000 * 60 * 60 * 24 * 14),
          });
        });
        this.incomingRequests
          .get(reqHash)!
          .push({ id: uuidv4(), promise, resolve, reject });
      }
    }

    if (!this.outgoingRequests.has(reqHash)) {
      const abortController = new AbortController();
      this.outgoingRequests.set(reqHash, { abortController });
      const onAbort = () => {
        abortController.signal.removeEventListener("abort", onAbort);
        this.outgoingRequests.delete(reqHash);
      };
      abortController.signal.addEventListener("abort", onAbort);
      (async () => {
        try {
          if (options.noCache) url = withQuery(url, { _no_cache: Date.now() });
          const request = fetch(url, {
            ...options,
            signal: abortController.signal,
          });
          const response = await request;
          if (!response.ok) {
            throw new Error(
              `Failed to fetch data, status: ${response.status}, url: '${url}'`,
            );
          }
          const json = await response.json();

          if (
            json?.status === "ERROR" &&
            (json?.subStatus === "FORBIDDEN" || json?.sub_status === "FORBIDDEN") &&
            // @ts-expect-error // TODO e - add types
            json?.errors?.some(e => e?.type === "TOKEN_INVALID")
          ) {
            const message =
              // @ts-expect-error // TODO e - add types
              json?.errors?.find(e => e?.type === "TOKEN_INVALID")?.message ||
              "Your session may have expired due to inactivity, or your token is invalid, please log in again";
            await store.dispatch(triggerGotUnauthorized(message));
            // Nothing to handle here — no promises to resolve and no risk of memory leaks,
            // since the signing page will reload anyway, so only return.
            return;
          }
          this.incomingRequests.get(reqHash)?.forEach(({ resolve }) => resolve(json));
          if (
            ["POST", "PUT", "DELETE"].includes(options?.method || "") &&
            json?.status !== "ERROR" &&
            !options?.noSideEffects
          ) {
            triggerDataRefresh();
          }
        } catch (error) {
          const isAbortError = error instanceof Error && error.name === "AbortError";
          const isAborted = abortController.signal.aborted;
          const isUnauthorizedError = error instanceof UnauthorizedError;

          if (!isAbortError && !isUnauthorizedError && !isAborted) {
            console.error("Error fetching data:", serializeError(error));

            let title = "request: ";
            title += options?.method || "GET";
            title += ": ";
            title += url;

            toast.error(humanizeError(error, title));
          }

          this.incomingRequests.get(reqHash)?.forEach(({ reject }) => reject(error));
        }
        abortController.signal.removeEventListener("abort", onAbort);
        this.incomingRequests.delete(reqHash);
        this.outgoingRequests.delete(reqHash);
      })()
        .then()
        .catch(e => console.error(e));
    }

    let resolve: DeferT["resolve"] = () => {
      throw new Error("Implementation error");
    };
    let reject: DeferT["reject"] = () => {
      throw new Error("Implementation error");
    };
    const promise = new CancelablePromise((resolve1, reject1, onCancel) => {
      resolve = resolve1;
      reject = reject1;
      onCancel(() => {
        const incomingRequests = this.incomingRequests.get(reqHash);
        const filteredIncomingRequests = incomingRequests?.filter(
          inspected => inspected.promise !== promise,
        );
        if (filteredIncomingRequests && filteredIncomingRequests.length > 1) {
          this.incomingRequests.set(reqHash, filteredIncomingRequests);
        }
        if (!filteredIncomingRequests?.length) {
          this.incomingRequests.delete(reqHash);
          if (this.outgoingRequests.has(reqHash)) {
            setTimeout(() => {
              if (
                !this.incomingRequests.get(reqHash)?.length &&
                this.outgoingRequests.has(reqHash)
              ) {
                this.outgoingRequests.get(reqHash)?.abortController.abort();
                this.outgoingRequests.delete(reqHash);
              }
            }, 1);
          }
        }
      });
    });

    if (!this.incomingRequests.get(reqHash)) this.incomingRequests.set(reqHash, []);

    this.incomingRequests
      .get(reqHash)!
      .push({ id: incomingRequestId, promise, resolve, reject });

    if (options?.channelId) {
      const prevRequestInChannel = this.channelMap.get(options?.channelId);
      if (prevRequestInChannel) prevRequestInChannel.cancel();
      this.channelMap.set(options?.channelId, promise);
      promise.then(() => {
        if (options?.channelId) this.channelMap.delete(options?.channelId);
      });
      promise.catch(() => {
        if (options?.channelId) this.channelMap.delete(options?.channelId);
      });
    }

    return promise;
  }
}

const requestCache1 = new RequestCache();

export default requestCache1.fetch.bind(requestCache1);
